import React, { useEffect, useState } from 'react';
import mixpanel from 'mixpanel-browser';

const GA_MEASUREMENT_ID = 'G-WNGL18FVLY';
export const CONSENT_KEY = 'zt_cookie_consent';
const MIXPANEL_TOKEN = 'f6f65fa66e7e807c80572e6d25f713e2';

/** Tracks whether mixpanel.init() has already been called this session. */
let mixpanelInitialised = false;

type ConsentValue = 'granted' | 'denied' | null;

/** Loads the Google Analytics script and fires the initial page-view. */
function loadGoogleAnalytics(): void {
    if (document.querySelector(`script[src*="${GA_MEASUREMENT_ID}"]`)) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Update consent state and configure GA after the script is available
    script.onload = () => {
        if (typeof (window as any).gtag === 'function') {
            (window as any).gtag('consent', 'update', {
                analytics_storage: 'granted',
            });
            (window as any).gtag('js', new Date());
            (window as any).gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });
        }
    };
}

/**
 * Initialises Mixpanel using the bundled npm package (mixpanel-browser).
 * Using the npm package avoids the fragile CDN script injection approach,
 * which is silently blocked by ad-blockers targeting cdn.mxpnl.com.
 * The SDK is bundled locally; only the data calls go to api-eu.mixpanel.com
 * (after the user grants consent), keeping this GDPR-compliant.
 *
 * Exported so callers (e.g. App.tsx) can guarantee init before calling
 * mixpanel.identify() — the npm package does not queue pre-init calls.
 */
export function initialiseMixpanel(): void {
    if (mixpanelInitialised) return;
    mixpanelInitialised = true;

    // Project uses EU data residency — route all events to the EU endpoint.
    // Disable automatic DOM Autocapture to avoid implementation errors
    // (some frameworks/virtual DOMs surface non-Element targets). Rely
    // on explicit tracking + SPA pageview tracking instead.
    mixpanel.init(MIXPANEL_TOKEN, {
        autocapture: false,
        track_pageview: 'full-url',
        record_sessions_percent: 100,
        api_host: 'https://api-eu.mixpanel.com',
    });
}

/** Reads saved consent from localStorage. */
function getSavedConsent(): ConsentValue {
    try {
        const saved = localStorage.getItem(CONSENT_KEY);
        if (saved === 'granted' || saved === 'denied') return saved;
    } catch {
        // localStorage may be unavailable in some contexts
    }
    return null;
}

/** Saves consent choice to localStorage. */
function saveConsent(value: 'granted' | 'denied'): void {
    try {
        localStorage.setItem(CONSENT_KEY, value);
    } catch {
        // ignore
    }
}

/**
 * GDPR cookie consent banner.
 * - Hidden once the user has already made a choice (persisted in localStorage).
 * - Accepting loads Google Analytics dynamically and updates the Consent Mode signal.
 * - Declining keeps GA blocked and records the denial.
 */
export const CookieConsent: React.FC = () => {
    const [consent, setConsent] = useState<ConsentValue>(getSavedConsent);

    // If consent was already granted in a previous session, load GA on mount.
    useEffect(() => {
        if (consent === 'granted') {
            loadGoogleAnalytics();
            initialiseMixpanel();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Banner is not shown once a decision has been recorded
    if (consent !== null) return null;

    const handleAccept = () => {
        saveConsent('granted');
        setConsent('granted');
        loadGoogleAnalytics();
        initialiseMixpanel();
    };

    const handleDecline = () => {
        saveConsent('denied');
        setConsent('denied');
        // Ensure Consent Mode is updated to denied (already the default, but be explicit)
        if (typeof (window as any).gtag === 'function') {
            (window as any).gtag('consent', 'update', { analytics_storage: 'denied' });
        }
    };

    return (
        <div className="cookie-consent-banner" role="dialog" aria-live="polite" aria-label="Cookie consent">
            <div className="cookie-consent-content">
                <p>
                    We use cookies to understand how Zeytune is used and to improve the service.
                    <br />
                    You can opt out at any time.{' '}
                    <a href="/policy" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                    </a>
                </p>
                <div className="cookie-consent-actions">
                    <button className="cookie-btn cookie-btn-decline" onClick={handleDecline}>
                        Decline
                    </button>
                    <button className="cookie-btn cookie-btn-accept" onClick={handleAccept}>
                        Accept analytics
                    </button>
                </div>
            </div>
        </div>
    );
};
